// SWR hook for the editor-entered turnout state.
//
// Mirrors useElectionDayEditorial / useElectionDaySampling:
//
//   Public-side: fetches /api/electionday-state?kind=turnout. The
//   endpoint reads from Postgres and is edge-cached (s-maxage=10,
//   stale-while-revalidate=30), so reader polls collapse into ~one
//   DB read per 10 s per region during peak counting-night load.
//
//   Admin-side: pass `admin: true` to read via the authenticated
//   endpoint with no caching, so the editor always sees their own
//   writes immediately.

import useSWR, { type SWRConfiguration } from 'swr';
import {
  emptyTurnoutState,
  type TurnoutState,
} from '../data/electionday-turnout-types';

const PUBLIC_URL = '/api/electionday-state?kind=turnout';
const ADMIN_URL = '/api/admin/electionday-turnout';

const fetcher = async (url: string): Promise<TurnoutState> => {
  const res = await fetch(url, { cache: 'no-store', credentials: 'include' });
  if (res.status === 404) return emptyTurnoutState();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TurnoutState;
};

export interface UseTurnoutOptions {
  admin?: boolean;
  refreshInterval?: number;
}

export function useElectionDayTurnout(
  options: UseTurnoutOptions = {},
): {
  data: TurnoutState;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
} {
  const url = options.admin ? ADMIN_URL : PUBLIC_URL;
  const config: SWRConfiguration = {
    refreshInterval: options.refreshInterval ?? 12_000,
    revalidateOnFocus: true,
    errorRetryCount: 2,
  };
  const { data, isLoading, error, mutate } = useSWR<TurnoutState>(
    url,
    fetcher,
    config,
  );
  return {
    data: data ?? emptyTurnoutState(),
    isLoading,
    error,
    mutate,
  };
}
